import React, { FC, Fragment, Suspense, useContext, useDeferredValue, useEffect, useState } from 'react';
import {
  SideSheet,
  SideSheetSize,
  SideSheetHeader,
  SideSheetClose,
  SideSheetBody,
  Text,
  Box,
  SideSheetFooter,
  Separator,
  SeparatorType,
  Spinner,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  WeldProcedure,
  StencilSelector,
  WeldTagItem,
  WeldTagSelector,
  RodRequestComments,
  useWeldProcedures,
  useWeldsByWeldProcedureQuery,
  CreateRodRequestToast,
  useCrewMemberWithSupervisor,
} from '@bechtel/client-weld-common';
import { CREATE_ROD_REQUEST_CADDY, GET_PROJECT_COMMODITY_TYPES_BY_ID } from '@bechtel/client-gql';
import {
  CrewMember,
  CrewMemberList,
  Weld,
  WeldProcedure as WeldProcedureType,
  CrewMemberDiscipline,
  CommodityFilterByTerm,
  FilterOperator,
  CreateRodRequestCaddyPayload,
  CreateRodRequestCaddyInput,
  RodRequestCaddy,
  CommodityComplexFilterInput,
  CreateRodrequestCaddyError,
  Project,
  CrewMemberSortByTerm,
} from '@bechtel/types';
import { ErrorBoundary, SuspenseLoader, useName, ErrorRetry, useAppUser } from '@bechtel/client-common';
import { t, Trans, Plural } from '@lingui/macro';
import { Formik, FormikProps } from 'formik';
import { map, findIndex, filter, get, find, toLower } from 'lodash';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { array, object, string } from 'yup';
import { FormikState } from './index';
import { RodRoomAttendantSelector } from '~/components/molecules';
import { SubHeaderStyled } from '~/request/styled';
import { AddCaddyDetail } from '~/request/detail/caddy/add/detail';
import { CaddyList } from '~/request/detail/caddy/list';
import { DeleteCaddy } from '~/request/detail/caddy/delete';
import {
  AddCaddyContainerStyled,
  FooterButtonStyled,
  SideSheetContentLeftColStyled,
  SideSheetContentRightColStyled,
} from './styled';
import { CreateRodRequestCaddyError, CreateRodRequestErrorType } from './error';
import { CreateAdhocRodRequestProps } from './types';
import { RodRoomDataContext } from '~/utilities/context';

const initialValues: FormikState = {
  weldProcedure: null,
  welds: [],
  welders: [],
  comments: '',
};

const validationSchema = object().shape({
  weldProcedure: object().required(t`Weld Procedure is mandatory`),
  welders: array()
    .required()
    .min(1, t`Select at least 1 welder`),
  welds: array()
    .required()
    .min(1, t`Select at least 1 Weld Tag`),
  comments: string().max(2000, t`Too Long!`),
});

export const CreateAdhocRodRequest: FC<CreateAdhocRodRequestProps> = ({ onClose }) => {
  const { getFullName } = useName();
  const { user } = useAppUser();
  const [caddies, setCaddies] = useState<RodRequestCaddy[]>([]);
  const [deleteCaddy, setDeleteCaddy] = useState<RodRequestCaddy | undefined>(undefined);
  const [issuedBy, setIssuedBy] = useState<CrewMember | undefined>(undefined);
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const [weldSearchQuery, setWeldSearchQuery] = useState<string>('');
  const debouncedWeldSearchQuery = useDeferredValue(weldSearchQuery);
  const [weldProcedureId, setWeldProcedureId] = useState<string>('');

  const { data: projectData } = useQuery<{
    projectsByIds: Project[];
  }>(GET_PROJECT_COMMODITY_TYPES_BY_ID, {
    variables: {
      ids: [user?.project?.id],
    },
    notifyOnNetworkStatusChange: true,
  });
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;

  const [
    getWelders,
    {
      loading: crewMemberLoading,
      error: crewMemberError,
      data: crewMemberData,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
    },
  ] = useCrewMemberWithSupervisor();
  const crewMemberList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];

  const [loadWeldProcedures, { data: weldProceduresData }] = useWeldProcedures();
  const weldProcedures = get(weldProceduresData, 'loadWeldProcedures.items', []) as WeldProcedureType[];

  const [loadWelds, { data: weldsData }] = useWeldsByWeldProcedureQuery(true);
  const weldList = map(get(weldsData, 'commodities.items', []), 'weld');

  const dispatchToast = useToastsDispatch();
  const [createRodRequestCaddyError, setCreateRodRequestCaddyError] = useState<CreateRodRequestErrorType | null>(null);
  const [errorCaddies, setErrorCaddies] = useState<CreateRodrequestCaddyError[] | null>(null);
  const [createRodRequestCaddy, { loading: mutationLoading }] = useMutation<
    { createRodRequestCaddy: CreateRodRequestCaddyPayload },
    { input: CreateRodRequestCaddyInput }
  >(CREATE_ROD_REQUEST_CADDY, {
    onCompleted: (data) => {
      const errorCaddies: CreateRodrequestCaddyError[] = get(data, 'createRodRequestCaddy.errorCaddies');
      if (errorCaddies.length) {
        setErrorCaddies(errorCaddies);
      } else {
        setCreateRodRequestCaddyError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => CreateRodRequestToast(get(data, 'createRodRequestCaddy.rodRequest.requestNumber', '')),
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        onClose();
      }
    },
    onError: (error) => {
      setCreateRodRequestCaddyError(error);
    },
    update: (cache) => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'rodRequests' });
      cache.gc();
    },
    refetchQueries: ['rodRequests', 'rodRoomStatsSummary'],
  });

  const onAddCaddy = (caddy: RodRequestCaddy) => {
    setCaddies((prev) => {
      const caddyIndex = findIndex(prev, (item) => {
        return item.caddy.id === caddy.caddy.id;
      });
      if (caddyIndex !== -1) return prev;
      return [...prev, caddy];
    });
  };

  const onDeleteCaddy = () => {
    setCaddies((prev) => {
      const caddyIndex = findIndex(prev, (item) => {
        return item.caddy.id === deleteCaddy?.caddy.id;
      });
      const previousCaddies = [...prev];
      if (caddyIndex !== -1) previousCaddies.splice(caddyIndex, 1);
      return previousCaddies;
    });
    setDeleteCaddy(undefined);
  };

  const onCloseErrorModal = () => {
    setCreateRodRequestCaddyError(null);
    setErrorCaddies(null);
  };

  useEffect(() => {
    getWelders({
      variables: {
        input: {
          discipline: CrewMemberDiscipline.WELDING,
          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
        },
      },
    });

    loadWeldProcedures({
      variables: {
        input: {
          searchKeyword: '',
        },
        paging: {
          cursor: '1',
          limit: 15,
        },
      },
    });

    loadWelds({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
                value: weldTypeId,
                operator: FilterOperator.EQUALS,
              },
            ],
          },
        },
      },
    });
  }, []);

  useEffect(() => {
    const weldComplexFilter: CommodityComplexFilterInput = {
      and: [
        //TODO: uncomment below code once BE starts providing data based on weld procedure
        // {
        //   term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        //   operator: FilterOperator.EQUALS,
        //   value: weldProcedureId
        // },
        // TODO: remove COMMODITY_TYPE_ID filter once BE starts providing welds by weld procedure
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
    };

    if (debouncedWeldSearchQuery)
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.TAG_NUMBER,
        value: debouncedWeldSearchQuery,
        operator: FilterOperator.CONTAINS,
      });

    loadWelds({
      variables: {
        input: {
          complexFilter: weldComplexFilter,
        },
      },
    });
  }, [debouncedWeldSearchQuery, weldProcedureId]);

  const onStencilItemChange = (welders: CrewMember[]) => {
    const input = {
      discipline: CrewMemberDiscipline.WELDING,
      sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
    };
    if (welders.length === 0) {
      getWelders({
        variables: {
          input,
        },
      });
    } else if (welders.length === 1 && welders[0].supervisor?.id) {
      getWelders({
        variables: {
          input: {
            ...input,
            supervisorId: welders[0].supervisor.id,
          },
        },
      });
    }
  };

  const onSearchCrewMember = (welders: CrewMember[], query) => {
    const input = {
      discipline: CrewMemberDiscipline.WELDING,
      sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
    };
    if (welders.length) {
      getWelders({
        variables: {
          input: {
            ...input,
            supervisorId: welders[0].supervisor?.id,
            searchKeyword: query,
          },
        },
      });
    } else {
      getWelders({
        variables: {
          input: {
            ...input,
            searchKeyword: query,
          },
        },
      });
    }
  };

  return (
    <SideSheet
      size={SideSheetSize.LARGE}
      visible
      style={{
        boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
      }}
    >
      <SideSheetHeader title={t`New Rod Request`}>
        <SideSheetClose onClick={() => onClose()} />
      </SideSheetHeader>
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(values) => {
          const rodRequest = {
            rodRoomId: selectedRodRoom?.id,
            weldProcedureId: values?.weldProcedure?.id,
            weldIds: map(values.welds, 'id'),
            stencilIds: map(values.welders, 'id'),
            comments: values.comments,
          };

          const caddiesPayload = map(caddies, (caddy) => {
            return {
              caddyId: caddy.caddy.id,
              fillerMaterialId: caddy.fillerMaterial.fillerMaterial.id,
              fillerMaterialDiameterDetailId: map(caddy.fillerMaterial.diameters, 'heatLotNumber.id'),
              issuedToStencilIds: map(caddy.stencils, 'id'),
              pickedUpBy: caddy?.pickedUpBy?.id ? caddy.pickedUpBy.id : '',
              issuedBy: (issuedBy as CrewMember).id,
            };
          });

          createRodRequestCaddy({
            variables: {
              input: {
                ...rodRequest,
                caddies: caddiesPayload,
              },
            },
          });
        }}
      >
        {(props: FormikProps<FormikState>) => {
          const { values, setFieldValue, dirty, isValid, handleSubmit } = props;
          const { welds, welders } = values;
          const selectedWeldersCount = welders.length;
          const welderList =
            welders.length > 0
              ? filter(crewMemberList, (item: CrewMember) => {
                  return item.supervisor?.id === welders[0].supervisor?.id;
                })
              : crewMemberList;

          return (
            <Fragment>
              <SideSheetBody>
                <SideSheetContentLeftColStyled>
                  <SubHeaderStyled>
                    <Text size="400" weight="600" family="graphik">
                      <Trans>Weld Procedure</Trans>
                    </Text>
                  </SubHeaderStyled>
                  <WeldProcedure
                    fieldName="weldProcedure"
                    items={weldProcedures}
                    onSelectedItemChange={(weldProcedureId) => setWeldProcedureId(weldProcedureId)}
                  />
                  <SubHeaderStyled>
                    <Text size="400" weight="600" family="graphik">
                      <Trans>Welder(s)</Trans>
                    </Text>
                  </SubHeaderStyled>
                  <Box px={{ sm: '450' }} py={{ sm: '500' }}>
                    {!crewMemberLoading && crewMemberError && (
                      <div data-testid="welder-error">
                        <ErrorRetry refetch={crewMemberRefetch}>
                          <Trans>There was a problem loading Stencils</Trans>
                        </ErrorRetry>
                      </div>
                    )}
                    {!crewMemberError && (
                      <StencilSelector
                        fieldName="welders"
                        items={welderList}
                        loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                        onSelectedItemsChange={(welders: CrewMember[]) => onStencilItemChange(welders)}
                        enableClientSearch={false}
                        onSearch={(query) => {
                          if (query.length > 0 && query.length < 2) return;
                          onSearchCrewMember(values.welders, query);
                        }}
                      />
                    )}
                  </Box>
                  {selectedWeldersCount > 0 && welders[0].supervisor && (
                    <Box px={{ sm: '550' }} pb={{ sm: '500' }}>
                      <Text size="300" family="graphik">
                        <Plural
                          value={selectedWeldersCount}
                          one={`${selectedWeldersCount} Welder selected is under`}
                          other={`${selectedWeldersCount} Welders selected are under`}
                        />
                      </Text>
                      &nbsp;
                      <Text size="300" family="graphik" weight="600">
                        {getFullName(welders[0].supervisor?.firstName, welders[0].supervisor?.lastName)}&nbsp;
                        <Trans>(FM)</Trans>
                      </Text>
                    </Box>
                  )}
                  <SubHeaderStyled>
                    <Text size="400" weight="600" family="graphik">
                      <Trans>Weld Tag(s)</Trans>
                    </Text>
                  </SubHeaderStyled>
                  <WeldTagSelector
                    fieldName="welds"
                    items={weldList}
                    onSearch={(searchQuery) => {
                      if (searchQuery.length === 0 || searchQuery.length >= 3) {
                        setWeldSearchQuery(searchQuery);
                      }
                    }}
                  />
                  <Box px={{ sm: '400' }} pt={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
                    {map(welds, (weldItem: Weld) => {
                      return (
                        <Box pb={{ sm: '500' }} key={weldItem.id}>
                          <WeldTagItem
                            item={weldItem}
                            showDelete={true}
                            onDelete={(weldId) => {
                              const newWelds = welds.filter((item: Weld) => item.id !== weldId);
                              setFieldValue('welds', newWelds);
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                  <SubHeaderStyled>
                    <Text size="400" weight="600" family="graphik">
                      <Trans>Comments</Trans>
                    </Text>
                  </SubHeaderStyled>
                  <RodRequestComments fieldName="comments" />
                </SideSheetContentLeftColStyled>
                <SideSheetContentRightColStyled disabled={!isValid || !dirty}>
                  <AddCaddyContainerStyled>
                    <Box pt={{ sm: '550' }} pl={{ sm: '500' }}>
                      <Text size="400" color="grey.600" weight="600">
                        <Trans>Add New Caddy</Trans>
                      </Text>
                    </Box>
                    <Box mr={{ sm: '450' }} mt={{ sm: '450' }}>
                      <Separator type={SeparatorType.HORIZONTAL} color="grey.300" />
                    </Box>
                    <AddCaddyDetail
                      welders={welders}
                      rodRoomId={selectedRodRoom?.id}
                      onAddCaddy={(caddy: RodRequestCaddy) => {
                        onAddCaddy(caddy);
                      }}
                      selectedCaddies={map(caddies, (caddy) => caddy.caddy)}
                    />
                  </AddCaddyContainerStyled>
                  {caddies.length > 0 && (
                    <Box mx={{ sm: '500' }} mt={{ sm: '500' }}>
                      <CaddyList
                        caddies={caddies}
                        onDeleteCaddy={(caddy: RodRequestCaddy) => {
                          setDeleteCaddy(caddy);
                        }}
                      />
                    </Box>
                  )}
                </SideSheetContentRightColStyled>
              </SideSheetBody>
              <SideSheetFooter>
                <Box ml={{ sm: '500' }} justifyContent={{ sm: 'space-between' }}>
                  <Box alignItems={{ sm: 'center' }}>
                    <Text color="white" size="300">
                      <Trans>Issued By:</Trans>
                    </Text>
                    <Box ml={{ sm: '300' }} style={{ width: '250px' }}>
                      <RodRoomAttendantSelector
                        selectedItem={issuedBy}
                        onRodRoomAttendantSelection={(selectedItem) => {
                          setIssuedBy(selectedItem);
                        }}
                      />
                    </Box>
                  </Box>
                  <Box alignItems={{ sm: 'center' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled onClick={onClose} disabled={mutationLoading}>
                        <Trans>Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <FooterButtonStyled
                      disabled={issuedBy === undefined || mutationLoading || caddies.length === 0 || !isValid || !dirty}
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      {mutationLoading ? <Spinner /> : <Trans>Issue Caddies</Trans>}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </Fragment>
          );
        }}
      </Formik>
      {deleteCaddy && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DeleteCaddy
              caddyNumber={deleteCaddy.caddy.label}
              onCancel={() => {
                setDeleteCaddy(undefined);
              }}
              onDelete={() => {
                onDeleteCaddy();
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {(createRodRequestCaddyError || errorCaddies) && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CreateRodRequestCaddyError
              error={createRodRequestCaddyError}
              errorCaddies={errorCaddies}
              onClose={onCloseErrorModal}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </SideSheet>
  );
};
